/**
 * Dashboard read model.
 *
 * <p>The portfolio totals come from `GET /v1/cases/summary`, which aggregates
 * in SQL over every open case the caller can see. They used to be derived in
 * the browser from one page of `/v1/cases` — which silently capped the whole
 * KPI row at the page size, and because that list is ordered by priority the
 * sample was the *worst* cases, so average DPD read high rather than merely
 * incomplete. Anything portfolio-wide belongs in the summary query; only
 * `topCases` still reads the list, and it genuinely wants the first few rows.</p>
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'
import { ageingBucket } from '@/lib/format'
import { useBatches } from '@/features/ingestion/api'
import { useCases } from '@/features/cases/api'

/** One bucket × priority-band cell, as the server groups it. */
export interface SummaryCell {
  bucket: string | null
  priorityBand: string
  count: number
  outstanding: string | number | null
}

/**
 * Open cases split by whether anything was collected since the dues file —
 * the same block, and the same rule, as the agent app's "My cases" card.
 * paidCount + unpaidCount = totalCount by construction on the server.
 */
interface CollectionProgressResponse {
  totalCount: number
  totalDue: string | number | null
  paidCount: number
  paidAmount: string | number | null
  unpaidCount: number
  unpaidDue: string | number | null
}

interface CaseSummaryResponse {
  openTotal: number
  p1Count: number
  p2Count: number
  totalOutstanding: string | number | null
  averageDpd: number | null
  asOfDate: string | null
  cells: SummaryCell[]
  /** Absent from an API older than the paid/unpaid split. */
  collection?: CollectionProgressResponse | null
}

export interface CollectionSplit {
  totalCount: number
  totalDue: number
  paidCount: number
  /** What has come in on the paid cases this cycle. */
  paidAmount: number
  unpaidCount: number
  /** What the unpaid cases still owe. */
  unpaidDue: number
}

export function useCaseSummary() {
  return useQuery({
    queryKey: ['cases', 'summary'],
    queryFn: async () => {
      const res = await api.get<CaseSummaryResponse>('/v1/cases/summary')
      return res.data
    },
  })
}

export interface BucketBreakdown {
  bucket: string
  label: string
  count: number
  outstanding: number
}

export interface DashboardSummary {
  openTotal: number
  p1Count: number
  p2Count: number
  totalOutstanding: number
  averageDpd: number | null
  asOfDate: string | null
  buckets: BucketBreakdown[]
  /** bucket × priority-band grid for the aging heatmap. */
  cells: SummaryCell[]
  /** Paid vs unpaid this cycle; null until loaded or from an older API. */
  collection: CollectionSplit | null
  topCases:ReturnType<typeof useCases>['data']
  recentBatches: ReturnType<typeof useBatches>['data']
  loading: boolean
  hasAnyCases: boolean
  hasAnyBatches: boolean
}

const BUCKETS: { key: string; label: string }[] = [
  { key: 'CURRENT',        label: '0' },
  { key: 'BUCKET_1_30',    label: '1–30' },
  { key: 'BUCKET_31_60',   label: '31–60' },
  { key: 'BUCKET_61_90',   label: '61–90' },
  { key: 'BUCKET_90_PLUS', label: '90+' },
]

const n = (v: string | number | null | undefined): number => {
  const parsed = typeof v === 'number' ? v : Number(v ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

export function useDashboardSummary(): DashboardSummary {
  const summary = useCaseSummary()
  // The dashboard shows the top six. It used to fetch 100 every 5 s, the
  // single largest source of traffic in the load tests; 30 s matches the
  // other dashboard tiles.
  const cases = useCases({ limit: 6 }, { refetchInterval: 30_000 })
  const batches = useBatches()

  return useMemo<DashboardSummary>(() => {
    const s = summary.data
    const batchRows = batches.data ?? []
    const cells = s?.cells ?? []

    // Roll the server's bucket × band grid up to bucket totals, so the
    // distribution card and the heatmap are two views of one query rather
    // than two independent counts. NPA and write-off cases fold into 90+ as
    // they do on the heatmap; without that they matched no bar and vanished.
    const bucketAgg = new Map<string, { count: number; outstanding: number }>()
    for (const c of cells) {
      const key = ageingBucket(c.bucket ?? 'BUCKET_1_30')
      const a = bucketAgg.get(key) ?? { count: 0, outstanding: 0 }
      a.count += c.count
      a.outstanding += n(c.outstanding)
      bucketAgg.set(key, a)
    }

    const buckets: BucketBreakdown[] = BUCKETS.map(({ key, label }) => ({
      bucket: key,
      label,
      count: bucketAgg.get(key)?.count ?? 0,
      outstanding: bucketAgg.get(key)?.outstanding ?? 0,
    }))

    return {
      openTotal: s?.openTotal ?? 0,
      p1Count: s?.p1Count ?? 0,
      p2Count: s?.p2Count ?? 0,
      totalOutstanding: n(s?.totalOutstanding),
      averageDpd: s?.averageDpd ?? null,
      asOfDate: s?.asOfDate ?? null,
      buckets,
      cells,
      collection: s?.collection
        ? {
            totalCount: s.collection.totalCount,
            totalDue: n(s.collection.totalDue),
            paidCount: s.collection.paidCount,
            paidAmount: n(s.collection.paidAmount),
            unpaidCount: s.collection.unpaidCount,
            unpaidDue: n(s.collection.unpaidDue),
          }
        : null,
      topCases:(cases.data ?? []).slice(0, 6),
      recentBatches: batchRows.slice(0, 5),
      loading: summary.isPending || batches.isPending,
      hasAnyCases: (s?.openTotal ?? 0) > 0,
      hasAnyBatches: batchRows.length > 0,
    }
  }, [summary.data, summary.isPending, cases.data, batches.data, batches.isPending])
}
